import { useEffect, useState } from "react";
import { HistoryIcon, SearchIcon } from "lucide-react";

import {
  FIELD_CONTENT_CLASS,
  FIELD_ICON_CLASS,
  FIELD_TRIGGER_CLASS,
  SadieSimulatorField,
  SadieSimulatorSwitchRow,
} from "./SadieSimulatorField";
import { SadieSimulatorOpportunityItem } from "./SadieSimulatorOpportunityItem";

import { Column } from "@/components/Column";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@ui/select";

import type { SadieSimulatorSearchOutcome } from "@/hooks/useSadieSimulator";
import {
  SadieSimulatorMatchedOpportunityFit,
  SadieSimulatorMatchedOpportunityOption,
} from "@/types";

export interface SadieSimulatorJobSearchSetup {
  onSearchOutcomeChange: (outcome: SadieSimulatorSearchOutcome) => void;
  onSearchedAtStartChange: (value: boolean) => void;
  searchOutcome: SadieSimulatorSearchOutcome;
  searchedAtStart: boolean;
}

// Not a fixture key, so it can never collide with a job.
const NOTHING_FOUND = "__nothing_found__";

// The same cap production puts on the reason carried into the prompt.
const PRIOR_REACTION_MAX_LENGTH = 120;

// What a job search finds when Sadie asks for one, for any session that can
// search: a fixture job, maybe one they were shown before and why they
// passed, or nothing. Fixture data only. Nothing is looked up.
export const SadieSimulatorJobSearchFields = ({
  alreadySearchedDescription,
  disabled,
  fitFor,
  matchedOpportunities,
  setup,
}: {
  // When the result reaches the prompt, in this session's terms.
  alreadySearchedDescription: string;
  disabled: boolean;
  fitFor: (
    option: SadieSimulatorMatchedOpportunityOption,
  ) => SadieSimulatorMatchedOpportunityFit | undefined;
  matchedOpportunities: SadieSimulatorMatchedOpportunityOption[];
  setup: SadieSimulatorJobSearchSetup;
}) => {
  /* React Hooks */
  const { searchOutcome } = setup;
  const savedPriorReaction =
    searchOutcome.status === "FOUND" ? (searchOutcome.priorReaction ?? "") : "";
  // Every change restarts the conversation, so the reason is only applied
  // when the tester is done typing it, not on each keystroke.
  const [priorReactionDraft, setPriorReactionDraft] =
    useState(savedPriorReaction);

  useEffect(() => {
    setPriorReactionDraft(savedPriorReaction);
  }, [savedPriorReaction]);

  /* Helpers */
  const searchValue =
    searchOutcome.status === "NONE"
      ? NOTHING_FOUND
      : (searchOutcome.opportunityKey ?? matchedOpportunities.at(0)?.key ?? "");

  /* Handlers */
  const handleSearchValueChange = (value: string) => {
    setup.onSearchOutcomeChange(
      value === NOTHING_FOUND
        ? { status: "NONE" }
        : {
            status: "FOUND",
            opportunityKey: value,
            previouslyShown:
              searchOutcome.status === "FOUND"
                ? searchOutcome.previouslyShown
                : false,
            priorReaction:
              searchOutcome.status === "FOUND"
                ? searchOutcome.priorReaction
                : undefined,
          },
    );
  };

  const handlePreviouslyShownChange = (value: boolean) => {
    if (searchOutcome.status !== "FOUND") return;
    setup.onSearchOutcomeChange({ ...searchOutcome, previouslyShown: value });
  };

  const commitPriorReaction = () => {
    if (searchOutcome.status !== "FOUND") return;
    const next = priorReactionDraft.trim();
    if (next === (searchOutcome.priorReaction ?? "")) return;
    setup.onSearchOutcomeChange({
      ...searchOutcome,
      priorReaction: next === "" ? undefined : next,
    });
  };

  /* Render */
  return (
    <Column className="gap-3">
      <SadieSimulatorField
        icon={<SearchIcon />}
        id="sadie-sim-search-outcome"
        label="When Sadie searches, it finds"
      >
        <Select
          disabled={disabled}
          onValueChange={handleSearchValueChange}
          value={searchValue}
        >
          <SelectTrigger
            className={FIELD_TRIGGER_CLASS}
            id="sadie-sim-search-outcome"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={FIELD_CONTENT_CLASS}>
            {matchedOpportunities.map((m) => (
              <SadieSimulatorOpportunityItem
                fit={fitFor(m)}
                isFitPending={false}
                key={m.key}
                option={m}
              />
            ))}
            <SelectSeparator />
            <SelectItem value={NOTHING_FOUND}>Nothing that fits</SelectItem>
          </SelectContent>
        </Select>
      </SadieSimulatorField>

      {searchOutcome.status === "FOUND" ? (
        <Column className="gap-2">
          <SadieSimulatorSwitchRow
            checked={searchOutcome.previouslyShown ?? false}
            description="Sadie pitched this job in an earlier session"
            disabled={disabled}
            icon={<HistoryIcon className={FIELD_ICON_CLASS} />}
            id="sadie-sim-previously-shown"
            label="Shown before"
            onCheckedChange={handlePreviouslyShownChange}
          />
          {searchOutcome.previouslyShown ? (
            <Input
              className="h-9 bg-white text-sm"
              disabled={disabled}
              maxLength={PRIOR_REACTION_MAX_LENGTH}
              onBlur={commitPriorReaction}
              onChange={(e) => {
                setPriorReactionDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitPriorReaction();
              }}
              placeholder="Why they passed last time (optional)"
              value={priorReactionDraft}
            />
          ) : null}
        </Column>
      ) : null}

      <SadieSimulatorSwitchRow
        checked={setup.searchedAtStart}
        description={alreadySearchedDescription}
        disabled={disabled}
        icon={<SearchIcon className={FIELD_ICON_CLASS} />}
        id="sadie-sim-searched-at-start"
        label="Already searched"
        onCheckedChange={setup.onSearchedAtStartChange}
      />
    </Column>
  );
};
